/**
 * CRM Navigation Types
 *
 * Types for the new Cloze-inspired CRM navigation system with:
 * - Left sidebar navigation
 * - Navigation toggle (tabs/sidebar/hidden focus mode)
 * - Three-panel layout support
 */

import { LucideIcon } from 'lucide-react';

/**
 * Navigation display modes
 * - tabs: Original horizontal tab navigation
 * - sidebar: New left sidebar navigation
 * - hidden: Focus mode with minimal navigation
 */
export type CRMNavigationMode = 'tabs' | 'sidebar' | 'hidden';

/**
 * Sidebar item configuration
 */
export interface SidebarItem {
  id: string;
  label: string;
  icon: LucideIcon;
  path?: string;
  badge?: number;
  badgeType?: 'count' | 'dot';
  children?: SidebarItem[];
  onClick?: () => void;
  disabled?: boolean;
  requiredPermission?: string;
}

/**
 * Sidebar state
 */
export interface SidebarState {
  isCollapsed: boolean;
  expandedGroups: string[];
  activeItemId: string | null;
}

/**
 * Three-panel layout configuration
 */
export interface PanelConfig {
  id: string;
  minWidth: number;
  maxWidth: number;
  defaultWidth: number;
  collapsible: boolean;
  visible: boolean;
}

/**
 * Panel state for resizable panels
 */
export interface PanelState {
  leftPanel: {
    width: number;
    collapsed: boolean;
  };
  rightPanel: {
    width: number;
    collapsed: boolean;
  };
}

/**
 * CRM Navigation context state
 */
export interface CRMNavigationState {
  mode: CRMNavigationMode;
  sidebar: SidebarState;
  panels: PanelState;
  isMobile: boolean;
  isTransitioning: boolean;
}

/**
 * CRM Navigation context actions
 */
export interface CRMNavigationActions {
  setMode: (mode: CRMNavigationMode) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setActiveItem: (itemId: string | null) => void;
  toggleExpandedGroup: (groupId: string) => void;
  setPanelWidth: (panel: 'left' | 'right', width: number) => void;
  togglePanel: (panel: 'left' | 'right') => void;
  resetToDefaults: () => void;
}

/**
 * Full CRM Navigation context type
 */
export interface CRMNavigationContextType extends CRMNavigationState, CRMNavigationActions {}

/**
 * User preferences for CRM navigation (stored in Firestore)
 */
export interface CRMUserPreferences {
  crmNavigationMode: CRMNavigationMode;
  sidebarCollapsed: boolean;
  agendaDefaultView: AgendaViewMode;
  panelWidths?: {
    left: number;
    right: number;
  };
}

/**
 * Agenda view modes
 */
export type AgendaViewMode = 'today' | 'week' | 'all';

/**
 * Agenda statistics
 */
export interface AgendaStats {
  today: number;
  next7Days: number;
  upcoming: number;
  followUp: number;
  pastDue: number;
}

/**
 * Agenda section types
 */
export type AgendaSectionType =
  | 'keep_in_touch'
  | 'meetings'
  | 'important_dates'
  | 'important_messages'
  | 'tasks';

/**
 * Agenda section configuration
 */
export interface AgendaSectionConfig {
  id: AgendaSectionType;
  label: string;
  icon: LucideIcon;
  collapsed: boolean;
  visible: boolean;
}

/**
 * Keep in Touch contact item
 */
export interface KeepInTouchItem {
  contactId: string;
  contactName: string;
  contactEmail?: string;
  contactPhone?: string;
  avatarUrl?: string;
  lastContactDate: Date;
  lastContactChannel: string;
  daysSinceContact: number;
  status: 'overdue' | 'due_soon' | 'on_track';
  onlineStatus?: 'online' | 'away' | 'offline';
}

/**
 * Meeting item for agenda
 */
export interface AgendaMeetingItem {
  id: string;
  title: string;
  attendeeEmails: string[];
  time: Date;
  duration: number;
  location?: string;
  propertyReference?: string;
  contactId?: string;
  contactName?: string;
  type: 'showing' | 'meeting' | 'consultation' | 'call';
}

/**
 * Important date item
 */
export interface ImportantDateItem {
  id: string;
  contactId: string;
  contactName: string;
  avatarUrl?: string;
  dateType: 'birthday' | 'anniversary' | 'sale_anniversary' | 'custom';
  label: string;
  date: Date;
  daysUntil: number;
  lastContactInfo?: string;
}

/**
 * Important message item
 */
export interface ImportantMessageItem {
  id: string;
  contactId: string;
  contactName: string;
  subject: string;
  preview: string;
  receivedAt: Date;
  isActionRequired: boolean;
  priority: 'high' | 'medium' | 'low';
  channel: 'email' | 'whatsapp' | 'sms';
}

/**
 * Filter options for agenda items
 */
export interface AgendaFilter {
  view: AgendaViewMode;
  sections: AgendaSectionType[];
  dateRange?: {
    start: Date;
    end: Date;
  };
}

/**
 * Navigation toggle button props
 */
export interface NavigationToggleProps {
  currentMode: CRMNavigationMode;
  onModeChange: (mode: CRMNavigationMode) => void;
  disabled?: boolean;
}

/**
 * Default panel widths
 */
export const DEFAULT_PANEL_WIDTHS = {
  sidebar: {
    collapsed: 70,
    expanded: 200,
  },
  leftPanel: {
    min: 250,
    max: 400,
    default: 320,
  },
  rightPanel: {
    min: 280,
    max: 450,
    default: 350,
  },
} as const;

/**
 * Default navigation state
 */
export const DEFAULT_NAVIGATION_STATE: CRMNavigationState = {
  mode: 'tabs', // Default to existing tab navigation for backward compatibility
  sidebar: {
    isCollapsed: false,
    expandedGroups: [],
    activeItemId: null,
  },
  panels: {
    leftPanel: {
      width: DEFAULT_PANEL_WIDTHS.leftPanel.default,
      collapsed: false,
    },
    rightPanel: {
      width: DEFAULT_PANEL_WIDTHS.rightPanel.default,
      collapsed: false,
    },
  },
  isMobile: false,
  isTransitioning: false,
};

/**
 * Local storage keys for persisting preferences
 */
export const CRM_STORAGE_KEYS = {
  navigationMode: 'crm_navigation_mode',
  sidebarCollapsed: 'crm_sidebar_collapsed',
  panelWidths: 'crm_panel_widths',
  agendaView: 'crm_agenda_view',
  expandedGroups: 'crm_expanded_groups',
} as const;
